import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { gql } from "../../../__generated__";
import type {
  FrequencyMismatchUser,
  OrderFrequencyByUserIdQuery,
} from "../../../__generated__/graphql";
import { getOrderStampCount } from "../../../utils/get-order-stamp-count";

type FrequencyOrder = NonNullable<
  OrderFrequencyByUserIdQuery["ordersForFrequencyCalculationByUserId"]
>[number];

function getStampCountForOrder(order: FrequencyOrder): number {
  if (!order.createdAt) {
    return 0;
  }

  return getOrderStampCount({
    amount: order.amount,
    createdAt: order.createdAt,
    updatedAt: order.updatedAt,
    orderConfirmedAt: order.orderConfirmedAt,
    packId: order.packId,
    isPackConfirmed: order.isPackConfirmed,
    isApproved: order.isApproved,
  });
}

const FREQUENCY_MISMATCH_USERS = gql(`
  query frequencyMismatchUsers {
    frequencyMismatchUsers {
      userId
      userName
      shouldBeFrequencyCount
      actualFrequencyCount
      orderId
      orderCreatedAt
      orderPickUpTime
      orderPackPickupEndTime
      thisOrderFreqCount
    }
  }
`);

const ORDER_FREQUENCY_BY_USER_ID = gql(`
  query orderFrequencyByUserId($userId: Int!) {
    ordersForFrequencyCalculationByUserId(userId: $userId) {
      id
      amount
      createdAt
      updatedAt
      orderConfirmedAt
      packId
      isPackConfirmed
      isApproved
      pickUpTime
      packPickupEndTime
      pack {
        id
        name
        price
      }
      products {
        id
        name
        price
        quantity
      }
    }
    orderFrequencyByUserId(userId: $userId) {
      id
      userId
      reason
      createdAt
      updatedAt
    }
  }
`);

const ISSUE_ORDER_FREQUENCY = gql(`
  mutation issueOrderFrequency($orderId: Int!) {
    issueOrderFrequency(orderId: $orderId) {
      ok
      error
    }
  }
`);

const AdminFrequencyMonitoring = () => {
  const [selectedUserId, setSelectedUserId] = useState<number | null>(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 프리퀀시 불일치 사용자 조회
  const { data: frequencyMismatchData, loading: frequencyMismatchLoading } =
    useQuery(FREQUENCY_MISMATCH_USERS);

  // 선택된 사용자의 주문 조회
  const { data: ordersData, loading: ordersLoading } = useQuery(
    ORDER_FREQUENCY_BY_USER_ID,
    {
      variables: { userId: selectedUserId || 0 },
      skip: !selectedUserId,
    }
  );

  // 프리퀀시 발급 mutation
  const [issueOrderFrequency] = useMutation(ISSUE_ORDER_FREQUENCY);

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString("ko-KR");
  };

  const handleUserIdClick = (userId: number) => {
    setSelectedUserId(userId);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUserId(null);
  };

  const handleIssueFrequency = async (order: FrequencyOrder) => {
    const frequencyCount = getStampCountForOrder(order);
    const confirmMessage = `사용자 ID ${selectedUserId}에게 ${frequencyCount}개의 프리퀀시를 발급할까요?`;

    if (window.confirm(confirmMessage)) {
      try {
        const result = await issueOrderFrequency({
          variables: { orderId: order.id },
        });

        if (result.data?.issueOrderFrequency.ok) {
          alert("프리퀀시가 성공적으로 발급되었습니다.");
          // 데이터 새로고침을 위해 쿼리 다시 실행
          window.location.reload();
        } else {
          alert(
            `프리퀀시 발급 실패: ${
              result.data?.issueOrderFrequency.error || "알 수 없는 오류"
            }`
          );
        }
      } catch (error) {
        console.error("프리퀀시 발급 중 오류:", error);
        alert("프리퀀시 발급 중 오류가 발생했습니다.");
      }
    }
  };

  return (
    <div className="w-full p-4">
      <h1 className="text-2xl font-bold mb-6">프리퀀시 모니터링</h1>

      {/* 프리퀀시 불일치 사용자 조회 */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">
          프리퀀시 불일치 사용자 조회
        </h2>

        {frequencyMismatchLoading && (
          <div className="text-center py-4">로딩 중...</div>
        )}

        {frequencyMismatchData?.frequencyMismatchUsers && (
          <>
            {/* 덜 지급된 사용자들 */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-3 text-red-600">
                덜 지급된 사용자들
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        사용자 ID
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        사용자명
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        예상 프리퀀시 수
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        실제 프리퀀시 수
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border-l-2 border-gray-300">
                        프리퀀시 차이
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        이 주문 프리퀀시 수
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        주문 ID
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        주문 생성일
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        실시간 팩 픽업시간
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        예약팩 종료시간
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {frequencyMismatchData.frequencyMismatchUsers
                      .filter((user: FrequencyMismatchUser) => {
                        const frequencyDifference =
                          user.shouldBeFrequencyCount -
                          user.actualFrequencyCount;
                        return frequencyDifference > 0;
                      })
                      .map((user: FrequencyMismatchUser, index: number) => {
                        const frequencyDifference =
                          user.shouldBeFrequencyCount -
                          user.actualFrequencyCount;
                        const isCorrect =
                          frequencyDifference === user.thisOrderFreqCount;

                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-4 whitespace-nowrap">
                              <button
                                onClick={() => handleUserIdClick(user.userId)}
                                className="text-blue-600 hover:text-blue-800 font-medium underline cursor-pointer"
                              >
                                {user.userId}
                              </button>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-gray-900">
                                {user.userName}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-gray-900 font-medium">
                                {user.shouldBeFrequencyCount}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-gray-900 font-medium">
                                {user.actualFrequencyCount}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap border-l-2 border-gray-300">
                              <div
                                className={`font-medium ${
                                  isCorrect ? "text-green-700" : "text-red-600"
                                }`}
                              >
                                {Math.abs(frequencyDifference)}개 덜지급됨
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div
                                className={`font-medium ${
                                  isCorrect ? "text-green-700" : "text-red-600"
                                }`}
                              >
                                {user.thisOrderFreqCount}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-gray-900">
                                {user.orderId || "-"}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-gray-900">
                                {user.orderCreatedAt
                                  ? formatDateTime(user.orderCreatedAt)
                                  : "-"}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-gray-900">
                                {user.orderPickUpTime || "-"}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-gray-900">
                                {user.orderPackPickupEndTime
                                  ? formatDateTime(user.orderPackPickupEndTime)
                                  : "-"}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 더 지급된 사용자들 */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-3 text-blue-600">
                더 지급된 사용자들
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        사용자 ID
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        사용자명
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        예상 프리퀀시 수
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        실제 프리퀀시 수
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border-l-2 border-gray-300">
                        프리퀀시 차이
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        이 주문 프리퀀시 수
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        주문 ID
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        주문 생성일
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        실시간 팩 픽업시간
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        예약팩 종료시간
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {frequencyMismatchData.frequencyMismatchUsers
                      .filter((user: FrequencyMismatchUser) => {
                        const frequencyDifference =
                          user.shouldBeFrequencyCount -
                          user.actualFrequencyCount;
                        return frequencyDifference < 0;
                      })
                      .map((user: FrequencyMismatchUser, index: number) => {
                        const frequencyDifference =
                          user.shouldBeFrequencyCount -
                          user.actualFrequencyCount;
                        const isCorrect =
                          frequencyDifference === user.thisOrderFreqCount;

                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-4 whitespace-nowrap">
                              <button
                                onClick={() => handleUserIdClick(user.userId)}
                                className="text-blue-600 hover:text-blue-800 font-medium underline cursor-pointer"
                              >
                                {user.userId}
                              </button>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-gray-900">
                                {user.userName}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-gray-900 font-medium">
                                {user.shouldBeFrequencyCount}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-gray-900 font-medium">
                                {user.actualFrequencyCount}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap border-l-2 border-gray-300">
                              <div
                                className={`font-medium ${
                                  isCorrect ? "text-blue-600" : "text-red-600"
                                }`}
                              >
                                {frequencyDifference}개 더 지급됨
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div
                                className={`font-medium ${
                                  isCorrect ? "text-blue-600" : "text-red-600"
                                }`}
                              >
                                {user.thisOrderFreqCount}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-gray-900">
                                {user.orderId || "-"}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-gray-900">
                                {user.orderCreatedAt
                                  ? formatDateTime(user.orderCreatedAt)
                                  : "-"}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-gray-900">
                                {user.orderPickUpTime || "-"}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-gray-900">
                                {user.orderPackPickupEndTime
                                  ? formatDateTime(user.orderPackPickupEndTime)
                                  : "-"}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* 주문 목록 모달 */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                사용자 ID: {selectedUserId} - 주문 목록
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            {ordersLoading && (
              <div className="text-center py-4">로딩 중...</div>
            )}

            <h3 className="text-lg font-semibold mb-3 text-gray-900">
              프리퀀시
            </h3>
            {ordersData?.orderFrequencyByUserId && (
              <div className="overflow-x-auto mb-4">
                <table className="min-w-full bg-white border border-gray-200">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        프리퀀시 ID
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        프리퀀시 생성일
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {ordersData.orderFrequencyByUserId.map(
                      (frequency: any, index: number) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-gray-900 font-medium">
                              {frequency.id}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-gray-900">
                              {frequency.createdAt
                                ? formatDateTime(frequency.createdAt)
                                : "-"}
                            </div>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <h3 className="text-lg font-semibold mb-3 text-gray-900">
              주문 내역
            </h3>
            {ordersData?.ordersForFrequencyCalculationByUserId && (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        주문 ID
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        주문 생성일
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        실시간 팩 픽업시간
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        예약팩 종료시간
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        팩 가격 (프리퀀시 개수)
                      </th>
                      <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        프리퀀시 발급
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {ordersData.ordersForFrequencyCalculationByUserId.map(
                      (order, index: number) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-gray-900 font-medium">
                              {order.id}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-gray-900">
                              {order.createdAt
                                ? formatDateTime(order.createdAt)
                                : "-"}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-gray-900">
                              {order.pickUpTime || "-"}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-gray-900">
                              {order.packPickupEndTime
                                ? formatDateTime(order.packPickupEndTime)
                                : "-"}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-gray-900">
                              {order.amount.toLocaleString()}{" "}
                              <span className="text-md">
                                ({getStampCountForOrder(order)}개)
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleIssueFrequency(order)}
                              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm"
                            >
                              발급
                            </button>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {ordersData?.ordersForFrequencyCalculationByUserId &&
              ordersData.ordersForFrequencyCalculationByUserId.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  주문 내역이 없습니다.
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFrequencyMonitoring;
